import { useState, useEffect } from 'react';
import { Drawer, Typography, Checkbox } from '@navi/web-ui/lib/primitives';
import SearchBarInput from '@navi/web-ui/lib/components/SearchBarInput';
import LinkIcon from '@cp/src/assets/icons/LinkIcon';
import { useDispatch, useSelector } from 'react-redux';
import { getColumnConfiguration, postColumnConfiguration } from '../action';
import { clearColumnConfigurationData } from '@cp/src/reducers/configureReportsSlice';
import { useForm, Controller } from 'react-hook-form';
import { getReportTypeDisplayName, getTeamTypeDisplayName, handleRedirection } from '../utils';
import { AvailableColumns, Column, ColumnFormData, ConfigFormDrawerProps } from '../interface';
import ConfigDrawerHeader from './ConfigDrawerHeader';
import ConfigDrawerFooter from './ConfigDrawerFooter';
import { RootState } from '@cp/src/store';
import ConfigDrawerInfo from './ConfigDrawerInfo';
import ConfigDrawerColumnList from './ConfigDrawerColumnList';

const ConfigFormDrawer = (props: ConfigFormDrawerProps) => {
  const { isDrawerOpen, onClose, isEditable, setIsEditable, selectedReport } = props;
  const dispatch = useDispatch();
  const [isOutsideClick, setIsOutsideClicked] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isDirty }
  } = useForm<ColumnFormData>({
    defaultValues: {
      columns: {},
      searchValue: ''
    }
  });

  const searchValue = watch('searchValue');

  const columnConfigurationData = useSelector(
    (state: RootState) => state.configureReport?.columnConfigurationData
  );
  const columnConfigurationLoading = useSelector(
    (state: RootState) => state.configureReport?.columnConfigurationLoading
  );

  const onEditClick = () => {
    setIsEditable(true);
  };

  const onCancel = () => {
    if (isDirty) {
      toggleOutsideClick();
    } else {
      onClose();
    }
  };

  const onSave = () => {
    handleSubmitClick();
  };

  const onExitYes = () => {
    handleExitYesClick();
  };

  const onExitNo = () => {
    handleExitNoClick();
  };

  useEffect(() => {
    if (isDrawerOpen && selectedReport) {
      const reportType = selectedReport.baseReportType;
      const teamType = selectedReport.teamType;
      dispatch(getColumnConfiguration(reportType, teamType));
    }

    return () => {
      if (!isDrawerOpen) {
        dispatch(clearColumnConfigurationData());
        reset({ columns: {}, searchValue: '' });
      }
    };
  }, [isDrawerOpen, selectedReport]);

  useEffect(() => {
    if (columnConfigurationData?.availableColumns) {
      const initialSelected: Record<string, boolean> = {};

      Object.entries(columnConfigurationData.availableColumns as AvailableColumns).forEach(
        ([category, columns]) => {
          columns.forEach((column: Column) => {
            initialSelected[column.columnName] = column.currentlySelected;
          });
        }
      );

      reset({
        columns: initialSelected,
        searchValue: ''
      });
    }
  }, [columnConfigurationData]);

  const onSubmit = (data: ColumnFormData) => {
    if (!selectedReport) {
      onClose();
      return;
    }

    const reportType = selectedReport.baseReportType;
    const teamType = selectedReport.teamType;

    const selectedColumns = Object.entries(data.columns)
      .filter(([, isSelected]) => Boolean(isSelected))
      .map(([columnName]) => columnName);

    if (props.onSubmitForm) {
      props.onSubmitForm({ reportType, teamType, selectedColumns });
    } else {
      dispatch(postColumnConfiguration(reportType, teamType, selectedColumns));
      setIsEditable(false);
      onClose();
    }
  };

  const handleSubmitClick = () => {
    handleSubmit(onSubmit)();
  };

  const toggleOutsideClick = () => {
    if (isDirty) {
      setIsOutsideClicked(true);
    } else {
      setIsOutsideClicked(false);
      onClose();
    }
  };

  const handleExitYesClick = () => {
    setIsOutsideClicked(false);
    reset();
    onClose();
  };

  const handleExitNoClick = () => {
    setIsOutsideClicked(false);
  };

  const filterColumns = (columns: Column[], category: string): Column[] => {
    if (!searchValue) return columns;

    if (category.toLowerCase().includes(searchValue.toLowerCase())) {
      return columns;
    }

    return columns.filter((column: Column) =>
      column.displayName.toLowerCase().includes(searchValue.toLowerCase())
    );
  };

  const highlightText = (text: string, searchTerm: string) => {
    if (!searchTerm) return text;

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);

    return (
      <>
        {parts.map((part, index) => {
          const isMatch = part.toLowerCase() === searchTerm.toLowerCase();
          return isMatch ? (
            <span
              key={index}
              style={{
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: 'var(--navi-color-blue-base)'
              }}
            >
              {part}
            </span>
          ) : (
            <span key={index}>{part}</span>
          );
        })}
      </>
    );
  };

  return (
    <Drawer
      open={isDrawerOpen}
      onClose={toggleOutsideClick}
      headerText={<ConfigDrawerHeader isEditable={isEditable} onEditClick={onEditClick} />}
      showHeaderDivider={true}
      usePadding={false}
      style={{ width: '360px' }}
      contentClasses="overflow-auto"
      onOutSideClick={toggleOutsideClick}
      disableOutsideClick={isDirty ? true : false}
      footerContainerClasses={'!p-0'}
      footer={
        <ConfigDrawerFooter
          isEditable={isEditable}
          isOutsideClick={isOutsideClick}
          onCancel={onCancel}
          onSave={onSave}
          onExitYes={onExitYes}
          onExitNo={onExitNo}
        />
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="px-4 py-4">
          <ConfigDrawerInfo
            baseReportType={selectedReport?.baseReportType || ''}
            teamType={selectedReport?.teamType || ''}
          />

          <div className="mb-4">
            <Controller
              name="searchValue"
              control={control}
              render={({ field }) => (
                <SearchBarInput
                  placeholder="Search columns..."
                  debounceDelay={150}
                  inputSize={'medium'}
                  containerClassName={'w-full'}
                  value={field.value}
                  onSearchChange={field.onChange}
                  disabled={!isEditable}
                />
              )}
            />
          </div>

          <ConfigDrawerColumnList
            columnConfigurationLoading={columnConfigurationLoading}
            columnConfigurationData={columnConfigurationData}
            control={control}
            isEditable={isEditable}
            searchValue={searchValue}
            filterColumns={filterColumns}
            highlightText={highlightText}
          />
        </div>
      </form>
    </Drawer>
  );
};

export default ConfigFormDrawer;
